"use client"

import { useState, useEffect, useRef } from "react"
import { useApi } from "@/lib/api"
import type { AutomationEmailsResponse } from "@/types/automation-email"

export function useAutomationEmails(itemId: string, isActive: boolean, isPanelOpen: boolean) {
  const { workflowApi } = useApi()
  const [emails, setEmails] = useState<AutomationEmailsResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const hasFetchedRef = useRef(false)

  useEffect(() => {
    // Reset cache when panel is closed or itemId changes
    if (!isPanelOpen || !itemId) {
      hasFetchedRef.current = false
      setEmails(null)
      setError(null)
      return
    }

    // Only fetch if we haven't fetched for this item yet and automation tab is active
    if (!hasFetchedRef.current && isActive) {
      const fetchEmails = async () => {
        setLoading(true)
        setError(null)
        
        try {
          const response = await workflowApi.get<AutomationEmailsResponse>(
            `/api/Workflow/automation-emails/${itemId}`
          )
          setEmails(response)
          hasFetchedRef.current = true
        } catch (err) {
          console.error("Failed to fetch automation emails:", err)
          setError(err instanceof Error ? err.message : "Failed to fetch automation emails")
          setEmails(null)
        } finally {
          setLoading(false)
        }
      }

      fetchEmails()
    }
  }, [itemId, isActive, isPanelOpen, workflowApi])

  return {
    emails: emails?.emails || [],
    loading,
    error,
  }
}

